'use client';

import { c as _c } from "react/compiler-runtime";
import { useApi } from '@repo/api';
import { useDebouncedValue } from '@repo/tanstack/pacer';
import { useQuery } from '@repo/tanstack/query';
import { useState } from 'react';
import { type CompanyTypeAlias, fromCompanySearchResult, getCompanyPickerError, getCompanyPickerStatus } from './company-picker-company.ts';
export type CompanySearchOptions = {
  /** The Organisation to search as (`org_id`), e.g. the Load Organisation for a 3PL's companies. */
  asOrganisationId?: null | number;
  /** Further conditions for searching, on top of the picker being open. */
  enabled?: boolean;
  /**
   * Companies never offered, such as the one already picked (picking it again never means a change)
   * or a load's 3PL Broker, which cannot be its own End Company.
   */
  excludeIds?: readonly (null | number | undefined)[];
  /** Keeps risk profiles on results, so a missing one shows as not verified. */
  includeRisk?: boolean;
  /** Only companies owned by this Organisation (`filter[organisation_id]`). */
  ownerOrganisationId?: null | number;
  /** Work outside the search, such as a save, that holds the picker and pauses searching. */
  pending?: boolean;
  /** Only companies of these Company types. */
  types?: readonly CompanyTypeAlias[];
};

/**
 * Company search for a company picker: open state, debounced search text, the `companies.search`
 * query (only while the picker is open), result mapping, status, error, and retry. Spread `picker`
 * onto `CompanyPicker` or `FormCompanyPicker`; `results` are the unmapped search rows, and `reset`
 * closes the picker and clears its search.
 */
export const useCompanySearch = t0 => {
  const $ = _c(21);
  const {
    asOrganisationId,
    enabled: t1,
    excludeIds: t2,
    includeRisk: t3,
    ownerOrganisationId,
    pending: t4,
    types
  } = t0 === undefined ? {} : t0;
  const enabled = t1 === undefined ? true : t1;
  const excludeIds = t2 === undefined ? [] : t2;
  const includeRisk = t3 === undefined ? false : t3;
  const pending = t4 === undefined ? false : t4;
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      wait: 300
    };
    $[0] = t5;
  } else {
    t5 = $[0];
  }
  const [debouncedSearch] = useDebouncedValue(search, t5);
  const {
    data,
    error,
    isFetching,
    refetch
  } = useQuery({
    ...api.companies.search.queryOptions({
      input: {
        "filter[organisation_id]": ownerOrganisationId ?? undefined,
        "filter[type]": types?.join(","),
        org_id: asOrganisationId ?? undefined,
        perPage: 20,
        search: debouncedSearch
      }
    }),
    enabled: open && enabled && !pending
  });
  const results = data?.data ?? [];
  const companies = results.filter(result => !excludeIds.includes(result.id)).map(result_0 => fromCompanySearchResult(result_0, {
    includeRisk
  }));
  let t6;
  if ($[1] !== error) {
    t6 = getCompanyPickerError(error);
    $[1] = error;
    $[2] = t6;
  } else {
    t6 = $[2];
  }
  let t7;
  if ($[3] !== refetch) {
    t7 = () => {
      refetch();
    };
    $[3] = refetch;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const t8 = getCompanyPickerStatus({
    isFetching,
    isPending: pending || search !== debouncedSearch
  });
  let t9;
  if ($[5] !== companies || $[6] !== open || $[7] !== search || $[8] !== setOpen || $[9] !== setSearch || $[10] !== t6 || $[11] !== t7 || $[12] !== t8) {
    t9 = {
      companies,
      error: t6,
      onOpenChange: setOpen,
      onRetry: t7,
      onSearchChange: setSearch,
      open,
      search,
      status: t8
    };
    $[5] = companies;
    $[6] = open;
    $[7] = search;
    $[8] = setOpen;
    $[9] = setSearch;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== setOpen || $[15] !== setSearch) {
    t10 = () => {
      setOpen(false);
      setSearch("");
    };
    $[14] = setOpen;
    $[15] = setSearch;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== results || $[18] !== t10 || $[19] !== t9) {
    t11 = {
      picker: t9,
      reset: t10,
      results
    };
    $[17] = results;
    $[18] = t10;
    $[19] = t9;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  return t11;
};